/**
 * Block Hydration Utilities
 *
 * Shared utilities for hydrating server-rendered blocks with React components.
 * Eliminates duplication across block initialization code.
 */
import { createRoot } from '@wordpress/element';
import type { ReactElement } from 'react';

/**
 * Base attributes interface - ensures attributes follow a predictable pattern
 */
export interface BlockAttributes {
	[key: string]: string | number | boolean | undefined;
}

/**
 * Options for extracting attributes from data attributes
 */
export interface AttributeExtractor<T extends BlockAttributes> {
	/**
	 * Extract attributes from an HTML element's dataset
	 * @param element - The HTML element containing data attributes
	 * @returns Typed attributes object
	 */
	extractAttributes: (element: HTMLElement) => T;
}

/**
 * Options for creating a block hydrator
 */
export interface BlockHydratorOptions<TAttributes extends BlockAttributes> {
	/**
	 * CSS selector to find blocks to hydrate
	 */
	selector: string;

	/**
	 * Function to extract attributes from the element's dataset
	 */
	extractAttributes: (element: HTMLElement) => TAttributes;

	/**
	 * Function to render the React component
	 * @param element - The HTML element being hydrated
	 * @param attributes - The extracted attributes
	 * @param initialContent - The initial server-rendered content
	 */
	render: (
		element: HTMLElement,
		attributes: TAttributes,
		initialContent: string
	) => ReactElement;

	/**
	 * Whether to wait for WordPress data stores to be ready
	 * @default true
	 */
	waitForStores?: boolean;

	/**
	 * Whether to preserve initial HTML content
	 * @default true
	 */
	preserveInitialContent?: boolean;

	/**
	 * Whether to hydrate all matching blocks regardless of enrollment status
	 * When true, hydrates ALL matching blocks (guest, logged-in, enrolled)
	 * When false (default), only hydrates based on selector (typically enrolled users)
	 * @default false
	 */
	hydrateAll?: boolean;
}

/**
 * Create a block hydrator function
 *
 * This utility eliminates the duplicate initialization patterns across blocks.
 * It handles:
 * - Waiting for WordPress data stores to be ready
 * - Finding blocks by selector
 * - Extracting attributes from data attributes
 * - Preserving initial server-rendered content
 * - Creating React roots and rendering components
 *
 * @param options - Hydrator configuration options
 * @returns A function that hydrates all matching blocks
 *
 * @example
 * ```tsx
 * // Display block (only enrolled users):
 * const initBlocks = createBlockHydrator({
 *   selector: '[data-enrollment-status="enrolled"][data-block="my-block"]',
 *   extractAttributes: (el) => ({
 *     text: el.dataset.text || '',
 *     level: Number(el.dataset.level) || 2,
 *   }),
 *   render: (el, attrs, initialContent) => (
 *     <MyComponent attributes={attrs} initialContent={initialContent} />
 *   ),
 * });
 *
 * // Interactive block (all users):
 * const initComplexBlocks = createBlockHydrator({
 *   selector: '.my-complex-block',
 *   hydrateAll: true,  // Hydrate for ALL enrollment statuses
 *   extractAttributes: (el) => ({ columns: Number(el.dataset.columns) || 3 }),
 *   render: (el, attrs) => <ComplexView attributes={attrs} />,
 * });
 *
 * // Auto-initialize on DOM ready
 * autoInitialize(initBlocks);
 * ```
 */
export function createBlockHydrator<TAttributes extends BlockAttributes>(
	options: BlockHydratorOptions<TAttributes>
): () => void {
	const {
		selector,
		extractAttributes,
		render,
		waitForStores = true,
		preserveInitialContent = true,
		hydrateAll = false,
	} = options;

	return function hydrateBlocks() {
		const blocks = document.querySelectorAll<HTMLElement>(selector);

		if (blocks.length === 0) {
			return;
		}

		const hydrateBlock = (block: HTMLElement) => {
			// Capture initial content before React mounts (if needed)
			const initialContent = preserveInitialContent ? block.innerHTML : '';

			// Extract attributes from data attributes
			const attributes = extractAttributes(block);

			// Create React root and render
			const root = createRoot(block);
			root.render(render(block, attributes, initialContent));
		};

		if (waitForStores) {
			// Wait for WordPress data stores to be ready
			waitForDataStores(() => {
				blocks.forEach(hydrateBlock);
			});
		} else {
			// Hydrate immediately
			blocks.forEach(hydrateBlock);
		}
	};
}

/**
 * Wait for WordPress data stores to be available
 *
 * Uses WordPress's subscribe API instead of polling for better performance.
 *
 * @param callback - Function to call when stores are ready
 */
export function waitForDataStores(callback: () => void): void {
	// Check if stores are already available
	if (typeof window.wp?.data?.select === 'function') {
		callback();
		return;
	}

	// Use subscribe to wait for data stores to become available
	// This is more efficient than polling with setTimeout
	let unsubscribe: (() => void) | undefined;
	let timeoutId: ReturnType<typeof setTimeout> | undefined;

	const checkAndRun = () => {
		if (typeof window.wp?.data?.select === 'function') {
			if (unsubscribe) unsubscribe();
			if (timeoutId) clearTimeout(timeoutId);
			callback();
		}
	};

	// Subscribe to WordPress data changes
	if (typeof window.wp?.data?.subscribe === 'function') {
		unsubscribe = window.wp.data.subscribe(checkAndRun);
	}

	// Fallback timeout in case subscribe doesn't work
	// Give up after 5 seconds to avoid infinite waiting
	timeoutId = setTimeout(() => {
		if (unsubscribe) unsubscribe();
		console.warn(
			'[Block Hydration] WordPress data stores not ready after 5 seconds. Hydrating anyway.'
		);
		callback();
	}, 5000);

	// Check immediately in case stores became available
	checkAndRun();
}

/**
 * Helper to convert dataset string to boolean
 *
 * @param value - The data attribute value
 * @param defaultValue - Default value if undefined
 * @returns Boolean value
 */
export function datasetToBoolean(
	value: string | undefined,
	defaultValue = false
): boolean {
	if (value === undefined) return defaultValue;
	return value === 'true';
}

/**
 * Helper to convert dataset string to number
 *
 * @param value - The data attribute value
 * @param defaultValue - Default value if undefined or invalid
 * @returns Number value
 */
export function datasetToNumber(
	value: string | undefined,
	defaultValue: number
): number {
	if (value === undefined) return defaultValue;
	const parsed = Number.parseInt(value, 10);
	return Number.isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Auto-initialize helper
 *
 * Calls the provided initialization function on DOM ready.
 *
 * @param initFn - The initialization function to call
 *
 * @example
 * ```tsx
 * autoInitialize(initMyBlocks);
 * ```
 */
export function autoInitialize(initFn: () => void): void {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initFn);
	} else {
		initFn();
	}
}
